/**
 * Patient-facing Fitness page (`/patient/fitness`).
 *
 * Read-only by default for almost everything; the only writes a fitness
 * client can make from the app are:
 *   • Update their own contact info (email/phone/address). Nothing else.
 *   • Self-hold membership (within plan's max_hold_days).
 *   • Self-renew membership (extends end_date + creates an invoice).
 *   • Sign the waiver of liability (one-time).
 *   • Book / cancel themselves into a class.
 *
 * Patients NEVER see clinical data here. This is the non-clinical wellness side.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Dumbbell, Calendar, Mail, Phone, MapPin, Edit3, X, CheckCircle2, AlertTriangle, Pause, RefreshCw, FileText, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from '../../components/SignaturePad';
import WellnessConsentGate from '../../components/WellnessConsentGate';
import { safeConfirm } from '../../lib/confirm';

export default function PatientFitness() {
  const [settings, setSettings] = useState(null);
  const [featureBlocked, setFeatureBlocked] = useState(false);
  const [profile, setProfile] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [classes, setClasses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [instances, setInstances] = useState([]);

  const load = () => {
    Promise.all([
      api.get('/clinic/settings').then(r => setSettings(r.data)),
      // /me returns 403 when feature_fitness=off — surface that as a dedicated
      // state so we render the disabled CTA instead of an infinite spinner.
      api.get('/fitness/clients/me')
        .then(r => { setProfile(r.data); setFeatureBlocked(false); })
        .catch(err => {
          if (err?.response?.status === 403) {
            setFeatureBlocked(true);
            setProfile({ enrolled: false });
          } else {
            setProfile({ enrolled: false });
          }
        }),
      api.get('/fitness/memberships/me').then(r => setMemberships(r.data)).catch(()=>{}),
      api.get('/fitness/assessments/me').then(r => setAssessments(r.data)).catch(()=>{}),
      api.get('/fitness/training-programs/me').then(r => setPrograms(r.data)).catch(()=>{}),
      api.get('/fitness/invoices/me').then(r => setInvoices(r.data)).catch(()=>{}),
      api.get('/fitness/classes').then(r => setClasses(r.data)).catch(()=>{}),
      api.get('/fitness/trainers').then(r => setTrainers(r.data)).catch(()=>{}),
      api.get('/fitness/instances').then(r => setInstances(r.data)).catch(()=>{}),
    ]);
  };
  useEffect(() => { load(); }, []);

  if (!settings) return <div className="p-8">Loading…</div>;
  if (!settings.feature_fitness || featureBlocked) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center" data-testid="patient-fitness-disabled">
        <Dumbbell className="w-12 h-12 mx-auto text-fg-muted/40 mb-3"/>
        <h1 className="text-2xl font-heading font-bold mb-2">Fitness module isn't enabled</h1>
        <p className="text-fg-muted text-sm">Ask the clinic if they offer fitness & wellness services.</p>
        <Link to="/patient" className="inline-block mt-4 text-brand underline text-sm">← Back to your dashboard</Link>
      </div>
    );
  }
  if (profile && profile.enrolled === false) {
    return <NotEnrolledYet/>;
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">WELLNESS</div>
        <h1 className="text-4xl font-heading font-bold tracking-tight inline-flex items-center gap-3">
          <Dumbbell className="w-8 h-8 text-brand"/> My fitness
        </h1>
        <p className="text-fg-muted mt-2">Manage your membership, book classes, and review your progress.</p>
      </div>

      {/* Iter 133 — Wellness consent gate. When the patient hasn't yet opted
          in to wellness_services_participation, this blocks access with a
          plain-English acceptance card. Once accepted collapses to a chip. */}
      <div className="mb-6">
        <WellnessConsentGate service="Fitness"/>
      </div>

      {profile && !profile.waiver_signed_at && profile.client_id && (
        <WaiverBlocker profile={profile} onSigned={load}/>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <ProfileCard profile={profile} onUpdated={load}/>
        <MembershipsCard memberships={memberships} onChange={load}/>
        <UpcomingPopupCard instances={instances} classes={classes}/>
      </div>

      {/* Calendar is the primary registration UI (iter 67) — full-width to make
          slot picking + booking the obvious path. */}
      <ClassesCalendarCard instances={instances} trainers={trainers} onChange={load}/>

      <div className="grid md:grid-cols-2 gap-4 my-6">
        <AssessmentsCard assessments={assessments}/>
        <TrainingProgramCard programs={programs}/>
      </div>

      <InvoicesCard invoices={invoices}/>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────

function NotEnrolledYet() {
  return (
    <div className="p-8 max-w-2xl mx-auto text-center">
      <Dumbbell className="w-16 h-16 mx-auto text-fg-muted/40 mb-4"/>
      <h1 className="text-3xl font-heading font-bold mb-2">Not enrolled in fitness yet</h1>
      <p className="text-fg-muted mb-4">
        You're registered for clinical services but haven't joined the wellness side of the clinic.
        Ask the front desk to enroll you — they'll send the waiver of liability for you to sign here.
      </p>
      <Link to="/patient" className="text-brand text-sm underline">← Back to your dashboard</Link>
    </div>
  );
}

function WaiverBlocker({ profile, onSigned }) {
  const [open, setOpen] = useState(false);
  const [sig, setSig] = useState('');
  const sign = async () => {
    if (!sig) return toast.error('Please sign before submitting.');
    try {
      await api.post(`/fitness/clients/${profile.client_id}/waiver`, {
        client_id: profile.client_id, signature: sig, version: 'v1-2026', locale: 'en',
      });
      setOpen(false);
      toast.success('Waiver signed.');
      onSigned?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  return (
    <>
      <div className="mb-5 rounded-xl p-4 border border-[#FFB000] bg-[#FFF4D6]" data-testid="waiver-blocker">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5 text-[#8A5A00] shrink-0"/>
          <div className="flex-1">
            <div className="text-sm font-medium text-[#8A5A00]">Waiver of liability required</div>
            <p className="text-xs text-[#5A3A00] mt-1">Before you can book a class or use any fitness service, please read and sign the waiver. This is legally distinct from your clinical informed-consent.</p>
            <button onClick={()=>setOpen(true)} className="btn-primary text-xs mt-2 inline-flex items-center gap-2" data-testid="waiver-open"><FileText className="w-3.5 h-3.5"/> Read &amp; sign</button>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-bg-primary rounded-lg max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-bold inline-flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-brand"/> Fitness waiver of liability
              </h3>
              <button onClick={()=>setOpen(false)}><X className="w-4 h-4"/></button>
            </div>
            <div className="text-sm text-fg-secondary space-y-3 max-h-[40vh] overflow-y-auto pr-2 mb-4">
              <p><strong className="text-fg">Acknowledgement of risk.</strong> I understand that participation in fitness activities (including but not limited to weight training, cardio, group classes, and personal training) involves inherent risks of physical injury including muscle strain, joint injury, fractures, and in rare cases more serious harm.</p>
              <p><strong className="text-fg">Voluntary participation.</strong> I freely accept these risks and confirm that I am voluntarily choosing to participate. I have disclosed any medical conditions to my trainer that may affect my safety.</p>
              <p><strong className="text-fg">Separation from clinical care.</strong> I acknowledge that fitness services are non-clinical and are NOT a substitute for physiotherapy, medical diagnosis, or treatment. Any clinical concerns will be addressed separately through the clinic's medical services under a separate contract.</p>
              <p><strong className="text-fg">Release of liability.</strong> I release the clinic, its trainers, and its staff from liability for any injury or loss arising from my participation, except where caused by gross negligence.</p>
              <p><strong className="text-fg">Data &amp; privacy.</strong> My fitness data (membership, attendance, assessments) is stored in compliance with GDPR. I may withdraw consent at any time, in which case my membership is terminated and personal data deleted.</p>
            </div>
            <SignaturePad onChange={setSig} testid="waiver-pad"/>
            <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-soft">
              <button onClick={()=>setOpen(false)} className="btn-secondary text-sm">Cancel</button>
              <button onClick={sign} disabled={!sig} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-40" data-testid="waiver-submit"><CheckCircle2 className="w-4 h-4"/> Sign &amp; submit</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ProfileCard({ profile, onUpdated }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  useEffect(() => { setForm({ email: profile?.email || '', phone: profile?.phone || '', address: profile?.address || '' }); }, [profile]);
  const save = async () => {
    try { await api.put('/fitness/clients/me', form); setEdit(false); toast.success('Contact info updated.'); onUpdated?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  if (!profile) return null;
  return (
    <div className="bento-card" data-testid="patient-fitness-profile">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold">My profile</h3>
        {!edit && <button onClick={()=>setEdit(true)} className="text-fg-muted hover:text-brand text-xs inline-flex items-center gap-1" data-testid="profile-edit"><Edit3 className="w-3.5 h-3.5"/> Edit</button>}
      </div>
      <div className="text-sm space-y-2">
        <div><span className="text-fg-muted text-xs uppercase">Name</span> <span className="text-fg ml-2">{profile.full_name}</span></div>
        {edit ? (
          <>
            <label className="block">
              <span className="text-fg-muted text-xs uppercase">Email</span>
              <input className="input mt-1" type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})}/>
            </label>
            <label className="block">
              <span className="text-fg-muted text-xs uppercase">Phone</span>
              <input className="input mt-1" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})}/>
            </label>
            <label className="block">
              <span className="text-fg-muted text-xs uppercase">Address</span>
              <input className="input mt-1" value={form.address} onChange={e=>setForm({...form, address:e.target.value})}/>
            </label>
            <div className="flex gap-2 pt-2">
              <button onClick={save} className="btn-primary text-xs flex-1" data-testid="profile-save">Save</button>
              <button onClick={()=>setEdit(false)} className="btn-secondary text-xs flex-1">Cancel</button>
            </div>
            <p className="text-[11px] text-fg-muted italic mt-1">You can only edit contact info. Anything else, please ask the front desk.</p>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-fg-muted"/><span>{profile.email || '—'}</span></div>
            <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-fg-muted"/><span>{profile.phone || '—'}</span></div>
            <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-fg-muted"/><span>{profile.address || '—'}</span></div>
            {profile.goals && <div className="text-xs text-fg-secondary mt-2 pt-2 border-t border-soft"><strong>Goals:</strong> {profile.goals}</div>}
          </>
        )}
      </div>
    </div>
  );
}

function MembershipsCard({ memberships, onChange }) {
  const active = memberships.find(m => m.status === 'active');
  const renew = async (m) => {
    if (!await safeConfirm(`Renew "${m.plan_name}" for ${m.duration_days} more days at ${m.price} ${m.currency}? An invoice will be generated.`)) return;
    try { await api.post(`/fitness/memberships/${m.membership_id}/renew`); toast.success('Renewed.'); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  const hold = async (m) => {
    const days = parseInt(window.prompt(`Hold for how many days? (max ${m.max_hold_days})`, '7'), 10);
    if (!days || days <= 0) return;
    try { await api.post(`/fitness/memberships/${m.membership_id}/hold`, { days }); toast.success('Hold applied.'); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  return (
    <div className="bento-card" data-testid="patient-fitness-memberships">
      <h3 className="font-heading font-semibold mb-3">Membership</h3>
      {!active ? (
        <p className="text-fg-muted text-sm">No active membership.</p>
      ) : (
        <div className="text-sm">
          <div className="text-lg font-medium text-brand">{active.plan_name}</div>
          <div className="text-xs text-fg-muted mt-1">{active.start_date} → <strong>{active.end_date}</strong></div>
          <div className="text-xs text-fg-muted mt-1">{active.classes_included === 0 ? 'Unlimited classes' : `${active.classes_included} classes / period`}</div>
          <div className="flex gap-2 mt-3 pt-3 border-t border-soft">
            <button onClick={()=>hold(active)} className="btn-secondary text-xs flex-1 inline-flex items-center justify-center gap-1" data-testid="membership-hold"><Pause className="w-3.5 h-3.5"/> Hold</button>
            <button onClick={()=>renew(active)} className="btn-primary text-xs flex-1 inline-flex items-center justify-center gap-1" data-testid="membership-renew"><RefreshCw className="w-3.5 h-3.5"/> Renew</button>
          </div>
        </div>
      )}
    </div>
  );
}

function UpcomingPopupCard({ instances, classes }) {
  const [open, setOpen] = useState(false);
  const me = useMe();
  const upcoming = (instances || []).filter(i => new Date(i.starts_at) >= new Date()).slice(0, 12);
  const bookedByMe = (i) => me && (i.bookings || []).includes(me.user_id);
  return (
    <div className="bento-card" data-testid="patient-upcoming-popup-card">
      <h3 className="font-heading font-semibold mb-3">Upcoming classes</h3>
      <p className="text-xs text-fg-muted mb-3">
        Quick overview of your next sessions — full registration happens in the calendar below.
      </p>
      <button onClick={()=>setOpen(true)} className="btn-secondary text-xs w-full" data-testid="patient-upcoming-popup-btn">
        View upcoming ({upcoming.length})
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="upcoming-modal">
          <div className="bg-bg-primary rounded-lg max-w-lg w-full p-6 shadow-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-heading font-bold">Upcoming classes</h3>
              <button onClick={()=>setOpen(false)} className="text-fg-muted hover:text-fg" data-testid="upcoming-modal-close">×</button>
            </div>
            <p className="text-xs text-fg-muted mb-3">
              To register, scroll down on the dashboard and pick a slot in the calendar — the calendar is the source of truth for class bookings.
            </p>
            {upcoming.length === 0 ? (
              <p className="text-fg-muted text-sm py-8 text-center">No classes scheduled.</p>
            ) : (
              <ul className="space-y-2">
                {upcoming.map(i => {
                  const cls = classes.find(c => c.class_id === i.class_id);
                  return (
                    <li key={i.instance_id} className="surface border border-soft rounded-md p-2.5 flex items-center justify-between gap-2" data-testid={`upcoming-item-${i.instance_id}`}>
                      <div className="text-xs flex-1 min-w-0">
                        <div className="font-medium truncate">{i.class_name}</div>
                        <div className="text-fg-muted">{new Date(i.starts_at).toLocaleString()} · tier-{cls?.tier || '?'}</div>
                      </div>
                      {bookedByMe(i) && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-brand whitespace-nowrap">✓ Booked</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="flex justify-end gap-2 pt-3 border-t border-soft mt-4">
              <button onClick={()=>setOpen(false)} className="btn-secondary text-sm">Close</button>
              <button onClick={()=>{ setOpen(false); document.querySelector('[data-testid="patient-classes-calendar"]')?.scrollIntoView({behavior:'smooth', block:'start'}); }}
                      className="btn-primary text-sm" data-testid="upcoming-go-to-calendar">
                Go to calendar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function NextClassesCard({ instances, classes, onChange }) {
  const me = useMe();
  const upcoming = (instances || []).filter(i => new Date(i.starts_at) >= new Date()).slice(0, 3);
  const bookedByMe = (i) => me && (i.bookings || []).includes(me.user_id);
  const book = async (i) => {
    try { await api.post(`/fitness/instances/${i.instance_id}/book`); toast.success('Booked!'); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  return (
    <div className="bento-card" data-testid="patient-next-classes">
      <h3 className="font-heading font-semibold mb-3">Upcoming classes</h3>
      {upcoming.length === 0 ? (
        <p className="text-fg-muted text-sm">No classes scheduled.</p>
      ) : (
        <ul className="space-y-2">
          {upcoming.map(i => {
            const cls = classes.find(c => c.class_id === i.class_id);
            const isBooked = bookedByMe(i);
            return (
              <li key={i.instance_id} className="surface border border-soft rounded-md p-2 flex items-center justify-between gap-2">
                <div className="text-xs">
                  <div className="font-medium">{i.class_name}</div>
                  <div className="text-fg-muted">{new Date(i.starts_at).toLocaleString()} · tier-{cls?.tier || '?'}</div>
                </div>
                {isBooked ? (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-brand">Booked</span>
                ) : (
                  <button onClick={()=>book(i)} className="btn-primary text-xs">Book</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function ClassesCalendarCard({ instances, trainers, onChange }) {
  const me = useMe();
  const bookedByMe = (i) => me && (i.bookings || []).includes(me.user_id);
  const future = (instances || []).filter(i => new Date(i.starts_at) >= new Date()).sort((a,b)=>new Date(a.starts_at)-new Date(b.starts_at));
  const book = async (i) => {
    try { await api.post(`/fitness/instances/${i.instance_id}/book`); toast.success('Booked!'); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  const cancel = async (i) => {
    try { await api.delete(`/fitness/instances/${i.instance_id}/book`); toast.success('Cancelled.'); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed.'); }
  };
  return (
    <div className="bento-card" data-testid="patient-classes-calendar">
      <h3 className="font-heading font-semibold mb-3 inline-flex items-center gap-2"><Calendar className="w-4 h-4 text-brand"/> Class calendar</h3>
      {future.length === 0 ? (
        <p className="text-fg-muted text-sm">No classes scheduled.</p>
      ) : (
        <ul className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
          {future.map(i => {
            const t = trainers.find(x => x.trainer_id === i.trainer_id);
            const full = (i.bookings || []).length >= i.capacity;
            const isBooked = bookedByMe(i);
            return (
              <li key={i.instance_id} className="surface border border-soft rounded-md p-3 flex items-center justify-between gap-2">
                <div className="text-sm">
                  <div className="font-medium">{i.class_name}</div>
                  <div className="text-xs text-fg-muted">{new Date(i.starts_at).toLocaleString()}{t ? ` · ${t.name}` : ''}</div>
                  <div className="text-[11px] text-fg-muted mt-0.5">{(i.bookings || []).length} / {i.capacity} booked</div>
                </div>
                {isBooked
                  ? <button onClick={()=>cancel(i)} className="btn-secondary text-xs">Cancel</button>
                  : full ? <span className="text-[10px] uppercase tracking-wider text-[#B83A3A]">Full</span>
                         : <button onClick={()=>book(i)} className="btn-primary text-xs">Book</button>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AssessmentsCard({ assessments }) {
  return (
    <div className="bento-card" data-testid="patient-assessments">
      <h3 className="font-heading font-semibold mb-3">Fitness assessments</h3>
      {(assessments || []).length === 0 ? (
        <p className="text-fg-muted text-sm">No assessments yet. Your trainer will record one at your next session.</p>
      ) : (
        <ul className="space-y-3">
          {assessments.map(a => (
            <li key={a.assessment_id} className="border-b border-soft pb-2 last:border-0">
              <div className="text-xs text-fg-muted">{a.date}</div>
              <div className="grid grid-cols-2 gap-1 text-xs mt-1">
                {a.weight_kg != null && <div><span className="text-fg-muted">Weight</span> {a.weight_kg} kg</div>}
                {a.body_fat_pct != null && <div><span className="text-fg-muted">Body fat</span> {a.body_fat_pct}%</div>}
                {a.resting_hr != null && <div><span className="text-fg-muted">Resting HR</span> {a.resting_hr} bpm</div>}
                {a.vo2_max != null && <div><span className="text-fg-muted">VO₂ max</span> {a.vo2_max}</div>}
                {a.pushup_count != null && <div><span className="text-fg-muted">Push-ups</span> {a.pushup_count}</div>}
                {a.plank_seconds != null && <div><span className="text-fg-muted">Plank</span> {a.plank_seconds}s</div>}
              </div>
              {a.notes && <div className="text-xs text-fg-secondary mt-1 italic">{a.notes}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TrainingProgramCard({ programs }) {
  const latest = (programs || [])[0];
  if (!latest) {
    return (
      <div className="admin-card mb-6" data-testid="patient-training-program">
        <h3 className="font-heading font-semibold mb-3">My training program</h3>
        <p className="text-fg-muted text-sm">No training program assigned yet. Your trainer will build one after your assessment.</p>
      </div>
    );
  }
  return (
    <div className="admin-card mb-6" data-testid="patient-training-program">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
        <div>
          <h3 className="font-heading font-semibold">{latest.title || 'My training program'}</h3>
          <p className="text-xs text-fg-muted">Week starting {latest.week_starts_on || (latest.created_at || '').slice(0,10)}</p>
        </div>
        {latest.notes && <p className="text-xs text-fg-secondary italic max-w-md">{latest.notes}</p>}
      </div>
      <table>
        <thead><tr><th>Exercise</th><th>Sets × Reps</th><th>Rest</th><th>Notes</th></tr></thead>
        <tbody>
          {(latest.exercises || []).map((ex, i) => (
            <tr key={`${ex.exercise_id}-${i}`}>
              <td className="text-sm">
                <div className="font-medium">{ex.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-fg-muted">
                  {(ex.primaryMuscles || []).join(' · ') || ex.level || ''}
                </div>
              </td>
              <td className="text-xs whitespace-nowrap">{ex.sets} × {ex.reps}</td>
              <td className="text-xs whitespace-nowrap">{ex.rest}</td>
              <td className="text-xs text-fg-muted">{ex.notes || ''}</td>
            </tr>
          ))}
          {!(latest.exercises || []).length && <tr><td colSpan="4" className="text-center py-6 text-fg-muted">No exercises in this program yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}


function InvoicesCard({ invoices }) {
  return (
    <div className="admin-card" data-testid="patient-fitness-invoices">
      <h3 className="font-heading font-semibold mb-3">My fitness invoices</h3>
      <table>
        <thead><tr><th>Date</th><th>Description</th><th>Total</th><th>Status</th></tr></thead>
        <tbody>
          {(invoices || []).map(i => (
            <tr key={i.fitness_invoice_id}>
              <td className="text-xs">{(i.created_at || '').slice(0, 10)}</td>
              <td className="text-xs">{i.items?.[0]?.name || i.type}</td>
              <td className="text-xs font-mono">{i.total} {i.currency}</td>
              <td><span className={i.status === 'paid' ? 'badge-success' : 'badge-neutral'}>{i.status}</span></td>
            </tr>
          ))}
          {!invoices?.length && <tr><td colSpan="4" className="text-center py-6 text-fg-muted">No invoices yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// useMe: tiny inline hook so child components can know their own user_id
// without re-fetching. Pulls from /auth/me once.
function useMe() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get('/auth/me').then(r => setMe(r.data)).catch(()=>{}); }, []);
  return me;
}
